import { createRoot } from "react-dom/client";
import App from "./App.jsx";
import { ErrorBoundary } from "./ErrorBoundary.jsx";
import { installOfflineOutbox } from "./offlineOutbox.js";
import { installOfflineSync } from "./offlineSync.js";
import { registerServiceWorker } from "./lib/push.js";

// Doit s'exécuter avant le premier rendu de App (donc avant le premier appel apiJSON,
// client/src/lib/api.js) — window.fetch doit être patché avant tout appel réseau de l'app.
installOfflineOutbox();
installOfflineSync();

// Jusqu'ici le service worker n'était enregistré QUE via le flux d'activation du Web Push
// (lib/push.js enable()/disable(), déclenché par un tap explicite) — la grande majorité des
// utilisateurs qui n'ont jamais activé les notifications n'avaient donc aucun shell PWA du
// tout. registerServiceWorker() est idempotent (mémoïse sa promesse) : l'appeler ici aussi,
// inconditionnellement au boot, n'entre pas en conflit avec l'appel existant dans push.js.
registerServiceWorker();

// Bug réel trouvé en test (2026-08-27, "le champ filiale se vide automatiquement") — DEUX
// causes successives, toutes les deux liées à un rechargement AUTOMATIQUE et non désiré :
//
// 1. clients.claim() (activate handler du SW) fait passer la page d'aucun contrôleur à un
//    contrôleur dès la TOUTE PREMIÈRE visite d'un appareil — ça déclenche "controllerchange"
//    même sans aucune vraie mise à jour de version. Un premier correctif (ne recharger que
//    si un contrôleur existait déjà) a résolu ce cas précis.
// 2. Mais tant que ce chantier reste en itération rapide (plusieurs déploiements staging
//    coup sur coup), CHAQUE réouverture — même après fermeture complète de l'app — détecte
//    une VRAIE nouvelle version par rapport à celle encore active sur l'appareil, et un
//    rechargement automatique se déclenchait alors légitimement... mais toujours sans
//    contrôle de l'utilisateur, pile pendant qu'il tape dans le formulaire de connexion.
//    Un rechargement forcé n'a donc jamais sa place ici, qu'il soit "justifié" ou non.
//
// Solution retenue : ne JAMAIS recharger tout seul. Le service worker prend le contrôle en
// arrière-plan (nécessaire pour que le Web Push reste fiable, voir service-worker.js) mais
// la page affichée ne change qu'au prochain rechargement voulu par l'utilisateur — un petit
// bandeau discret (jamais superposé au formulaire) propose de recharger, jamais imposé.
if ("serviceWorker" in navigator) {
  const hadControllerAlready = Boolean(navigator.serviceWorker.controller);

  function showUpdateBanner() {
    if (document.getElementById("nxUpdateBanner")) return;
    const banner = document.createElement("button");
    banner.id = "nxUpdateBanner";
    banner.type = "button";
    banner.textContent = "Nouvelle version disponible — appuyer pour recharger";
    banner.style.cssText = [
      "position:fixed", "left:50%", "bottom:16px", "transform:translateX(-50%)",
      "z-index:99999", "background:#0d1730", "color:#fff", "border:none",
      "padding:10px 16px", "border-radius:999px", "font:14px/1.3 system-ui,sans-serif",
      "box-shadow:0 6px 20px rgba(0,0,0,.25)", "cursor:pointer"
    ].join(";");
    banner.addEventListener("click", () => window.location.reload());
    document.body.appendChild(banner);
  }

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (hadControllerAlready) showUpdateBanner();
  });
}

createRoot(document.getElementById("root")).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
